import { ChangeDetectionStrategy, Component, Input, OnInit } from '@angular/core';

import { combineLatest, Observable, of, filter, map, startWith } from 'rxjs';

import { FareFamily, FinnairBoundItem, TranslatedBenefitRow } from '@fcom/dapi/api/models';
import { finShare } from '@fcom/rx';
import { FareFamilyMap } from '@fcom/common/interfaces/booking';

export const mapRebookPolicy = (fareFamily: FareFamily): TranslatedBenefitRow | undefined =>
  fareFamily?.translatedBenefitRows.find((row) => row.key === 'rebook');

export const mapCancellationPolicy = (fareFamily: FareFamily): TranslatedBenefitRow | undefined =>
  fareFamily?.translatedBenefitRows.find((row) => row.key === 'cancel');

interface TranslatedBenefitRowBounds {
  outbound: TranslatedBenefitRow | undefined;
  inbound?: TranslatedBenefitRow;
}

@Component({
  selector: 'fin-offer-policy',
  templateUrl: './offer-policy.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class OfferPolicyComponent implements OnInit {
  @Input()
  fareFamilies$: Observable<FareFamilyMap>;
  @Input()
  outbound$: Observable<FinnairBoundItem>;
  @Input()
  inbound$: Observable<FinnairBoundItem>;
  @Input()
  isOneWay$: Observable<boolean>;
  @Input()
  showHighlightedContent$: Observable<boolean> = of(false);

  outboundBrandName$: Observable<string>;
  inboundBrandName$: Observable<string>;

  rebookAndCancellationPolicies$: Observable<{
    rebook: TranslatedBenefitRowBounds;
    cancel: TranslatedBenefitRowBounds;
  }>;

  constructor() {
    /* noop */
  }

  ngOnInit(): void {
    this.outboundBrandName$ = combineLatest([
      this.outbound$.pipe(filter((v) => !!v)),
      this.fareFamilies$.pipe(filter(Boolean)),
    ]).pipe(
      map(([outbound, fareFamilies]: [FinnairBoundItem, FareFamilyMap]) => {
        return this.fareFamilyBrandName(outbound, fareFamilies);
      }),
      filter((v) => !!v)
    );

    this.inboundBrandName$ = combineLatest([
      this.inbound$.pipe(filter((v) => !!v)),
      this.fareFamilies$.pipe(filter(Boolean)),
      this.isOneWay$,
    ]).pipe(
      filter(([, , isOneWay]) => !isOneWay),
      map(([inbound, fareFamilies]: [FinnairBoundItem, FareFamilyMap, boolean]) => {
        return this.fareFamilyBrandName(inbound, fareFamilies);
      }),
      filter((v) => !!v)
    );

    this.rebookAndCancellationPolicies$ = combineLatest([
      this.fareFamilies$.pipe(filter(Boolean)),
      this.outbound$,
      this.inbound$.pipe(startWith(null as FinnairBoundItem)),
    ]).pipe(
      map(([fareFamilies, outbound, inbound]) => ({
        rebook: {
          outbound: mapRebookPolicy(fareFamilies[outbound?.fareFamily?.code]),
          inbound: inbound ? mapRebookPolicy(fareFamilies[inbound?.fareFamily?.code]) : undefined,
        },
        cancel: {
          outbound: mapCancellationPolicy(fareFamilies[outbound?.fareFamily?.code]),
          inbound: inbound ? mapCancellationPolicy(fareFamilies[inbound?.fareFamily?.code]) : undefined,
        },
      })),
      finShare()
    );
  }

  private fareFamilyBrandName(outbound: FinnairBoundItem, fareFamilies: FareFamilyMap) {
    const brandName = outbound.fareFamily.name;
    const code = outbound.fareFamily.code;
    return brandName ? brandName : (fareFamilies[code] || ({} as FareFamily)).brandName;
  }
}
